<template>
  <div :class="['UIToast', `UIToast__${position}`]">
    <TransitionGroup name="UIToast__item">
      <div
        v-for="toast in toastStore.toasts"
        :key="toast.id"
        :class="['UIToast__item', `UIToast__item-${toast.type}`]"
      >
        <div class="UIToast__item-content">
          <UIIcon class="UIToast__item-icon" :name="iconMap[toast.type]" />
          <div class="UIToast__item-text">
            <h5 v-if="toast.title" class="UIToast__item-title">
              {{ toast.title }}
            </h5>
            <div v-html="DOMPurify.sanitize(toast.message)" />
          </div>
          <UIButtonIcon
            icon="close"
            class="UIToast__close-icon"
            @click="() => toastStore.removeToast(toast.id)"
          />
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'

import DOMPurify from 'dompurify'

import { useToast } from '@/store/toast'
import UIButtonIcon from '@/views/components/ui/buttons/UIButtonIcon.vue'

import { TOAST_POSITION } from './types'
import UIIcon from '../icons/UIIcon.vue'

import type { TOAST_TYPE } from './types'
import type { IIcons } from '../icons/types'

const iconMap: Record<TOAST_TYPE, IIcons> = {
  success: 'checkCircle',
  warning: 'checkCircle',
  error: 'exclamationTriangleOutline',
  caution: 'exclamationCircleOutline',
  info: 'exclamationCircleOutline',
  basic: 'accountOutline',
}
const toastStore = useToast()

defineProps({
  position: {
    type: String as PropType<TOAST_POSITION>,
    default: TOAST_POSITION.TOP_CENTER,
  },
})
</script>

<style lang="scss" scoped>
.UIToast {
  z-index: 2000;
  position: fixed;
  padding: 1rem;
  max-width: 43rem;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  pointer-events: none;

  &__item {
    position: relative;
    pointer-events: auto;
    background: $color-white;
    box-shadow: 0px 5px 10px -2px #09092d26;
    border-radius: $border-radius-lg;
    opacity: 1;
    padding: 0.5rem 1rem;
    width: 100%;
    max-width: 100%;
    line-height: 1.3rem;
    margin-bottom: 1rem;

    &-icon {
      --size: 1.5rem;
      --color: var(--toast-color, $color-neutral-170);
      margin-right: 0.5rem;
    }

    &-content {
      @include font-size-16px;
      color: var(--toast-color, $color-neutral-170);
      display: flex;
      align-items: center;
      width: 100%;
      min-height: 2.25rem;

      .UIIcon {
        --size: 1.5rem;
      }
      .UIButtonIcon {
        --icon-size: 1.25rem;
        margin-left: 0.25rem;
      }
    }

    &-title {
      margin-bottom: 0.35rem;
      font-size: 1rem;
    }

    &-text {
      width: 100%;
      font-weight: 500;

      :deep(a) {
        color: $color-neutral-170;
        text-decoration: none;
      }
    }

    @mixin toastTheme($color) {
      --toast-color: #{$color};
    }

    &-success {
      @include toastTheme($color-success-70);
    }
    &-error {
      @include toastTheme($color-error-70);
    }
    &-info {
      @include toastTheme($color-info-70);
    }
    &-warning {
      @include toastTheme($color-warning-70);
    }
    &-caution {
      @include toastTheme($color-caution-70);
    }
    &-basic {
      @include toastTheme($color-neutral-170);
    }

    // UIToast item animation
    &-move,
    &-enter-active,
    &-leave-active {
      transition: all 0.5s ease;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateX(1.5rem);
    }
  }

  &__top-right {
    top: 0rem;
    right: 0rem;
  }

  &__top-left {
    top: 0rem;
    left: 0rem;
  }

  &__top-center {
    top: 0rem;
    left: 0;
    right: 0;
    margin: 0 auto;
  }

  &__bottom-left {
    bottom: 0rem;
    left: 0rem;
  }

  &__bottom-right {
    bottom: 0rem;
    left: 0rem;
  }

  .UIToast__close-icon {
    --icon-size: 1rem;
    --icon-color: #{$color-neutral-140};
  }
}
</style>
